import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import './Button.css';

export type ButtonVariant =
  | 'primary'   // sodium fill — one per screen region, the thing to do
  | 'default'   // outlined, the workhorse
  | 'quiet'     // no chrome until hovered — toolbars, row actions
  | 'danger';   // destructive, outlined in alert; never a red fill

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading glyph. Kept separate from children so it never inherits text truncation. */
  icon?: ReactNode;
  /** Icon-only. Requires `aria-label`; enforced by the types below. */
  iconOnly?: boolean;
  /** Renders the busy state without changing the button's width. */
  busy?: boolean;
  fullWidth?: boolean;
}

/**
 * Buttons never shrink below the width of their label. Spanish labels
 * run ~25% longer than English and Russian longer still, so width is
 * content-driven with a `min-inline-size` floor in `ch` — a fixed
 * pixel width is the single most common way a translated admin breaks.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    icon,
    iconOnly = false,
    busy = false,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx('pd-btn', className)}
      data-variant={variant}
      data-size={size}
      data-icon-only={iconOnly || undefined}
      data-busy={busy || undefined}
      data-full={fullWidth || undefined}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon ? (
        <span className="pd-btn__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {!iconOnly && children ? <span className="pd-btn__label">{children}</span> : null}
    </button>
  );
});

/** Groups buttons into a single machined block with shared borders. */
export function ButtonGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('pd-btn-group', className)} role="group">
      {children}
    </div>
  );
}
